import {
	type LoaderFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from '@remix-run/node'
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useNavigate,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { ChevronDown, Shield } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type HoneypotInputProps } from 'remix-utils/honeypot/server'
import { toast } from 'sonner'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { HeaderBreadcrumb } from './components/header-breadcrumb.tsx'
import { ImpersonationBanner } from './components/impersonation-banner.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { RoleSwitcher } from './components/role-switcher.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { SidebarTrigger } from './components/ui/sidebar.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserImpersonationDialog } from './components/user-impersonation-dialog.tsx'
import Layout from './layout.tsx'
import herobg from './routes/_marketing+/logos/momentumlogo.png'
import { useTheme } from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { getDomainUrl, getUserImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import {
	getDisabledRoles,
	getImpersonationState,
	type ImpersonationState,
} from './utils/role-session.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Momentum' : 'Error | Momentum' },
		{ name: 'description', content: `Your workout log` },
	]
}

export type RootLoaderData = {
	isMobile: boolean
	user: {
		image: {
			id: string
		} | null
		id: string
		username: string
		name: string | null
		ownGroups: {
			image: {
				id: string
			} | null
			id: string
			name: string
		}[]
		groups: {
			image: {
				id: string
			} | null
			id: string
			name: string
		}[]
		roles: {
			name: string
		}[]
	} | null
	// Role switching and impersonation state
	disabledRoles: string[]
	impersonation: ImpersonationState | null
	// If impersonating, this contains the admin's info
	adminUser: {
		id: string
		username: string
		name: string | null
	} | null
	requestInfo: {
		hints: { theme: Theme }
		origin: string
		path: string
		userPrefs: {
			theme: ReturnType<typeof getTheme>
		}
	}
	ENV: ReturnType<typeof getEnv>
	toast: Awaited<ReturnType<typeof getToast>>['toast']
	honeyProps: HoneypotInputProps
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const timings = makeTimings('root loader')
	const authenticatedUserId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// These depend only on the request (or merely on whether a user is logged
	// in), not on each other — resolve them together instead of in series.
	const [impersonation, disabledRoles, { toast, headers: toastHeaders }] =
		await Promise.all([
			authenticatedUserId
				? getImpersonationState(request, authenticatedUserId)
				: Promise.resolve({
						isImpersonating: false,
						impersonatingUserId: null,
						originalUserId: null,
					}),
			authenticatedUserId ? getDisabledRoles(request) : Promise.resolve([]),
			getToast(request),
		])

	// Determine the effective user ID (impersonated user or authenticated user)
	const effectiveUserId =
		impersonation.isImpersonating && impersonation.impersonatingUserId
			? impersonation.impersonatingUserId
			: authenticatedUserId

	// The effective user and the impersonating admin both depend only on the
	// impersonation state resolved above — fetch them in parallel.
	const [user, adminUser] = await Promise.all([
		effectiveUserId
			? time(
					() =>
						prisma.user.findUniqueOrThrow({
							select: {
								id: true,
								name: true,
								username: true,
								ownGroups: {
									where: { archivedAt: null },
									select: {
										name: true,
										id: true,
										image: { select: { id: true } },
									},
								},
								groups: {
									where: { archivedAt: null },
									select: {
										name: true,
										id: true,
										image: { select: { id: true } },
									},
								},
								image: { select: { id: true } },
								roles: {
									select: {
										name: true,
									},
								},
							},
							where: { id: effectiveUserId },
						}),
					{ timings, type: 'find user', desc: 'find user in root' },
				)
			: Promise.resolve(null),
		impersonation.isImpersonating && impersonation.originalUserId
			? prisma.user.findUnique({
					select: {
						id: true,
						username: true,
						name: true,
					},
					where: { id: impersonation.originalUserId },
				})
			: Promise.resolve(null),
	])

	if (effectiveUserId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const honeyProps = honeypot.getInputProps()

	return new Response(
		JSON.stringify({
			isMobile: /iPhone|iPad|iPod|Android/.test(
				request.headers.get('user-agent') ?? '',
			),
			user,
			disabledRoles,
			impersonation: impersonation.isImpersonating ? impersonation : null,
			adminUser,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		}),
		{
			headers: {
				'Content-Type': 'application/json',
				'Server-Timing': timings.toString(),
				...(toastHeaders instanceof Headers
					? Object.fromEntries(toastHeaders.entries())
					: toastHeaders),
			},
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
		'Cache-Control': loaderHeaders.get('Cache-Control') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'dark',
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		/* overflow-hidden, not just overflow-x: the app scrolls inside #main-scroll,
		   but overflow-x-hidden alone makes overflow-y compute to auto, giving the
		   root a phantom scrollbar — measured at 2720px of empty scroll below a
		   fixed-height shell. */
		<html
			lang="en"
			className="h-full overflow-hidden"
			data-theme={theme === 'light' ? 'light' : undefined}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				{/*
				 * `interactive-widget=resizes-content` makes the software
				 * keyboard SHRINK the layout viewport instead of overlaying it.
				 * Without it the keyboard covers the page, the browser scrolls
				 * the focused field into view itself, and on dismissal it does
				 * not put anything back — leaving the result editor offset with
				 * dead space where the keyboard had been.
				 *
				 * With it, `h-dvh` shrinks while the keyboard is up and grows
				 * back when it goes down, so the layout recovers on its own.
				 * `initial-scale=1` with no `maximum-scale` is deliberate:
				 * pinch-zoom stays available. The zoom-on-focus problem is
				 * solved by sizing controls at 16px, not by forbidding zoom.
				 */}
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,interactive-widget=resizes-content"
				/>
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>() as RootLoaderData
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const pathname = useLocation().pathname
	const isLoginRoute = pathname.includes('login')
	const isAuthRoute =
		isLoginRoute ||
		pathname.startsWith('/signup') ||
		pathname.startsWith('/onboarding') ||
		pathname.startsWith('/forgot-password') ||
		pathname.startsWith('/reset-password') ||
		pathname.startsWith('/verify')
	const isMarketingRoute =
		pathname === '/' ||
		pathname === '/about' ||
		pathname === '/coach' ||
		pathname === '/how-to' ||
		pathname === '/reference' ||
		pathname === '/support' ||
		pathname === '/privacy' ||
		pathname === '/tos'
	const hideGlobalHeader = isAuthRoute || isMarketingRoute
	const navigate = useNavigate()
	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)
	const [rolePaneOpen, setRolePaneOpen] = useState(false)

	// The app scrolls inside this container, not the window, so Remix's
	// <ScrollRestoration /> never sees the position — reset it ourselves
	// whenever the route (pathname, not search params) changes.
	const mainScrollRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		mainScrollRef.current?.scrollTo(0, 0)
	}, [pathname])

	/**
	 * Put the DOCUMENT back after the software keyboard closes.
	 *
	 * The viewport meta asks the keyboard to resize the layout rather than
	 * overlay it, which fixes this at the source — but only on browsers that
	 * honour `interactive-widget`. Where it is ignored, iOS covers the page
	 * with the keyboard, scrolls the focused field into view by shifting the
	 * document, and then does not undo that shift when the keyboard goes
	 * away. The result is the editor sitting offset with dead space below it,
	 * which only a device rotation cleared.
	 *
	 * Zeroing it is safe rather than a guess: `html` is `overflow-hidden` and
	 * the app scrolls inside `#main-scroll`, so the document has nothing of
	 * its own to scroll and any non-zero offset here IS the artifact. The
	 * container's own scroll position is deliberately left alone — that one
	 * is the user's.
	 *
	 * Deferred a frame because the shift lands after `focusout`; reading and
	 * resetting in the same tick restores a value iOS then overwrites.
	 */
	useEffect(() => {
		let frame = 0
		const restoreDocumentScroll = () => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(() => {
				if (window.scrollX !== 0 || window.scrollY !== 0) {
					window.scrollTo(0, 0)
				}
			})
		}
		window.addEventListener('focusout', restoreDocumentScroll)
		return () => {
			cancelAnimationFrame(frame)
			window.removeEventListener('focusout', restoreDocumentScroll)
		}
	}, [])

	// Global network-error toast. The client fetch wrapper (entry.client) emits
	// `app:network-error` when a mutation POST fails to reach the server; raise
	// the toast here, where the app's sonner Toaster instance lives.
	useEffect(() => {
		const onNetworkError = () =>
			toast.error(
				"Network error — your changes weren't saved. Check your connection and try again.",
			)
		window.addEventListener('app:network-error', onNetworkError)
		return () => window.removeEventListener('app:network-error', onNetworkError)
	}, [])

	// Role switching and impersonation are admin-only tools, so the whole Roles
	// control — trigger button included — is admin-only chrome.
	const isAdmin = user?.roles?.some((role) => role.name === 'admin') ?? false
	const hasMultipleRoles = (user?.roles?.length ?? 0) > 1
	const isImpersonating = data.impersonation?.isImpersonating ?? false

	// Update the document element's data-theme attribute when theme changes
	useEffect(() => {
		const root = document.documentElement
		root.classList.remove('light', 'dark')
		if (theme === 'light') {
			root.setAttribute('data-theme', 'light')
		} else {
			root.removeAttribute('data-theme')
		}
	}, [theme])

	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			//@ts-ignore
			env={data.ENV}
		>
			{/* Impersonation banner - shown when admin is impersonating a user */}
			{isImpersonating && user && data.adminUser && (
				<ImpersonationBanner
					impersonatingUser={{
						username: user.username,
						name: user.name,
					}}
					adminUser={data.adminUser}
				/>
			)}
			<Layout>
				{/* Add top padding when impersonation banner is visible */}
				{/*
				 * `h-dvh`, NOT `h-screen`. On iOS `100vh` is the LARGE viewport
				 * height — the page as it would be with Safari's toolbar
				 * retracted — so in the browser this shell is always taller
				 * than the area actually on screen. That slack is scrollable
				 * even though `html` is `overflow-hidden` (see Document), and
				 * when the software keyboard opens Safari scrolls the document
				 * into it to lift the caret, then fails to clamp the offset
				 * back to zero on dismiss: the page is left pushed up with
				 * empty background below it until a rotation forces a resize.
				 * `100dvh` tracks the viewport that is actually visible, so
				 * there is no slack to be stranded in. Installed to the home
				 * screen there is no toolbar, dvh == vh, and the bug never
				 * appeared — which is what identified the toolbar slack as the
				 * cause.
				 */}
				<div
					className={`flex h-dvh w-full flex-col justify-start ${isImpersonating ? 'pt-12' : ''}`}
				>
					{hideGlobalHeader ? null : (
						<header className="px-2 py-4">
							{user ? (
								<div className="flex w-full items-center justify-between gap-2">
									{/* min-w-0 lets this group shrink below its content width; without it
									    a long breadcrumb label pushes the header wider than the viewport
									    and the whole page scrolls sideways on mobile. */}
									<div className="flex min-w-0 flex-1 items-center gap-4">
										<SidebarTrigger className="w-[50px] shrink-0" />
										<HeaderBreadcrumb />
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{isAdmin ? (
											rolePaneOpen ? (
												<div className="w-102 absolute right-14 top-0 z-50 mt-2 flex justify-evenly gap-2 rounded-md border bg-popover p-2 shadow-lg">
													{/* Role switcher — admins only, and only when they
													    actually hold more than the base user role */}
													{hasMultipleRoles && !isImpersonating && (
														<RoleSwitcher
															roles={user.roles}
															disabledRoles={data.disabledRoles}
														/>
													)}
													{/* Impersonation dialog (not when already impersonating) */}
													{!isImpersonating && <UserImpersonationDialog />}
													<Button
														variant="default"
														size="sm"
														className="gap-1"
														onClick={() => setRolePaneOpen((open) => !open)}
													>
														<Shield className="h-4 w-4" />
														<span className="hidden sm:inline">Roles</span>
														<ChevronDown className="h-3 w-3" />
													</Button>
												</div>
											) : (
												<Button
													variant="outline"
													size="sm"
													className="gap-1"
													onClick={() => setRolePaneOpen((open) => !open)}
												>
													<Shield className="h-4 w-4" />
													<span className="hidden sm:inline">Roles</span>
													<ChevronDown className="h-3 w-3" />
												</Button>
											)
										) : null}
										{isLoginRoute ? (
											<Logo />
										) : user ? (
											<img
												src={getUserImgSrc(user.image?.id)}
												className="h-10 w-10 cursor-pointer rounded-full object-cover"
												onClick={() => navigate(`/dashboard`)}
											/>
										) : null}
									</div>
								</div>
							) : (
								<nav className="flex items-center justify-between gap-4 md:gap-8">
									<div className="flex items-center gap-10">
										<Button asChild variant="default" size="lg">
											<Link to="/login" reloadDocument>
												Log In
											</Link>
										</Button>
									</div>
								</nav>
							)}
						</header>
					)}
					<div
						id="main-scroll"
						ref={mainScrollRef}
						className="h-full min-w-full overflow-auto"
					>
						<Outlet />
					</div>
				</div>
			</Layout>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</Document>
	)
}

function Logo() {
	return (
		<Link to="/">
			<img src={herobg} className="aspect-square h-[40px] w-[40px]" />
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>() as RootLoaderData
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
